import React, { useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import { Icon } from "@iconify/react";
import { ColDef, GridApi } from "ag-grid-community";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  Error,
  HStack,
  VStack,
} from "../../components/utils";
import { Input, Label, TextArea } from "../../components/shared/InputField";
import { getEmployeesFromExcel } from "../../api/TransactionApisV2";
import convertToBase64 from "../../utils/convertToBase64";
import {
  useCreateDesignatedPerson,
  useUpdateDesignatedPerson,
} from "../../queries/designatedPerson";
import { DesignatedPersonEmployee } from "../../types/designatedPerson";
import { useEmployees } from "../../queries/employees";
import { CTADropdown } from "../../components/shared/Dropdown";
import GenericTableHeader from "../../shared/GenericTableHeader";
import Tooltip from "../../components/shared/Tooltip";

function AddOrEditDesignatedPersons({
  mode,
  data,
  onClose,
}: {
  mode: "Edit" | "Add";
  data?: any;
  onClose: () => void;
}) {
  const gridApi = useRef<GridApi | null>(null);
  const onGridReady = (params: any) => {
    gridApi.current = params;
  };
  const [affectedPersons, setAffectedPersons] = useState<
    DesignatedPersonEmployee[]
  >(data?.employeesList || []);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const gridData = useMemo(() => affectedPersons, [affectedPersons]);
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError(null);
    setUploadLoading(true);
    const file = e.target.files?.[0];
    if (!file) return;
    convertToBase64(file)
      .then((base64) => {
        getEmployeesFromExcel(base64 as string)
          .then((res) => {
            if (res) {
              const filterPersons = res.data.filter(
                (emp: any) =>
                  !affectedPersons.some((e) => e.employeeId === emp.id)
              );
              setAffectedPersons((prev) => [...prev, ...filterPersons]);
            }
          })
          .catch((err) => {
            toast.error(
              err?.response?.data?.errorRaw ||
                err?.response?.data.errorMessage ||
                err?.message ||
                "Failed to send reminders."
            );
            setUploadLoading(false);
          });
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.errorRaw ||
            err?.response?.data.errorMessage ||
            err?.message ||
            "Failed to send reminders."
        );
        setUploadLoading(false);
      });
  };

  const { data: _employees } = useEmployees();
  const allPersons = _employees || [];
  const filteredPersons = allPersons.filter((emp: any) => {
    const searchText = search.trim().toLowerCase();
    return (
      searchText &&
      (emp.employeeName?.toLowerCase().includes(searchText) ||
        emp.email?.toLowerCase().includes(searchText) ||
        emp.employeeIdentificationString?.toLowerCase().includes(searchText))
    );
  });

  const handleAddPerson = (emp: any) => {
    if (!affectedPersons.find((e) => e.employeeId === emp.id)) {
      setAffectedPersons((prev) => [
        ...prev,
        {
          employeeId: emp.id,
          name: emp.employeeName,
          email: emp.email,
          preApproved: false,
        },
      ]);
    }
  };
  const handleRemovePerson = (emp: any) => {
    setAffectedPersons((prev) =>
      prev.filter((e) => e.employeeId !== emp.employeeId)
    );
  };

  function handleAction(
    action: { name: string; disabled?: boolean },
    employee: DesignatedPersonEmployee
  ) {
    if (action.name === "Delete Employee" && !action.disabled) {
      handleRemovePerson(employee);
    }
  }

  const personColDefs: ColDef<any>[] = useMemo(
    () => [
      {
        headerName: "Name",
        field: "name",
        flex: 1,
        menuTabs: [],
        sortable: true,
      },
      {
        headerName: "Email",
        field: "email",
        flex: 1,
        menuTabs: [],
        sortable: true,
      },
      {
        headerName: "",
        field: "actions",
        width: 120,
        menuTabs: [],
        onCellClicked: (params: any) => {
          handleRemovePerson(params.data);
        },
        cellRenderer: (params: any) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleRemovePerson(params.data);
            }}
            title="Delete Employee"
            style={{ background: "none", border: "", cursor: "pointer" }}
          >
            <Icon icon="mdi:delete" className="w-5 h-5 text-black" />
          </button>
        ),
      },
    ],
    []
  );

  const initialValues = {
    name: data?.name || "",
    affectedPersons: data?.employeeList || [],
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
  });

  const { mutate: addDesignatedPerson, isLoading: isAddLoading } =
    useCreateDesignatedPerson();
  const { mutate: editDesignatedPerson, isLoading: isEditLoading } =
    useUpdateDesignatedPerson();

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      if (affectedPersons.length === 0) {
        toast("Please select at least one person", { type: "error" });
        return;
      }
      const payload = { ...values, affectedPersons };
      if (mode === "Add") {
        addDesignatedPerson(
          { name: values.name, employeesList: affectedPersons },
          {
            onSuccess: () => {
              toast("Designated person added successfully!", {
                type: "success",
              });
              onClose();
            },
            onError: (err: any) => {
              toast(err.response.data.reason, {
                type: "error",
                autoClose: 2000,
              });
            },
          }
        );
      } else {
        editDesignatedPerson(
          { id: data.id, name: values.name, employeesList: affectedPersons },
          {
            onSuccess: () => {
              toast("Designated person edited successfully!", {
                type: "success",
              });
              onClose();
            },
            onError: (err: any) => {
              toast(err.response.data.reason, {
                type: "error",
                autoClose: 2000,
              });
            },
          }
        );
      }
    },
  });

  return (
    <Box className="text-lg font-medium border-b py-7">
      <h6 className="flex justify-between px-6 pb-4 border-b">
        {mode === "Edit" ? "Edit Designated Person" : "Add Designated Person"}
        <span onClick={onClose} className="cursor-pointer">
          ✕
        </span>
      </h6>
      <VStack className="w-full gap-4 px-6 pt-4">
        <HStack className="items-center gap-4">
          <div className="flex-1">
            <Label className="required">List Name</Label>
            <Input type="text" {...formik.getFieldProps("name")} />
            {formik.touched.name && formik.errors.name && (
              <Error text={formik.errors.name as string} />
            )}
          </div>
          <div className="flex-1"></div>
        </HStack>
        {/* Affected Persons Section */}
        <VStack className="w-full gap-2 pt-2">
          <HStack className="justify-between gap-2">
            <HStack className="items-center gap-2">
              <GenericTableHeader
                heading={"Affected Employees"}
                subHeading={""}
                customClassName="flex flex-col px-0 lg:justify-start"
                additionalInfo={<>{`${affectedPersons?.length} employees`}</>}
              ></GenericTableHeader>
            </HStack>
            <HStack className="items-center gap-4">
              <HStack className="items-center justify-end gap-4 text-center">
                <HStack className="items-center ">
                  <Tooltip text="Upload an Excel file with Hr Ids">
                    <ButtonPrimary1
                      className=""
                      onClick={() =>
                        document.getElementById("fileInput")?.click()
                      }
                    >
                      <Icon
                        icon="mdi:upload-outline"
                        className="w-4 h-4 mr-2"
                      />
                      Upload Excel
                    </ButtonPrimary1>
                  </Tooltip>
                  <input
                    id="fileInput"
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      handleFileUpload(e);
                      e.target.value = "";
                    }}
                  />
                </HStack>
              </HStack>
              <HStack className="text-sm text-center">Or</HStack>
              <HStack className="gap-2 pt-2 ">
                <Input
                  placeholder="Search and Add employee"
                  value={search}
                  onChange={(e: any) => setSearch(e.target.value)}
                  className="w-96"
                />
                {search && (
                  <Box className="absolute z-10 overflow-y-auto bg-white border border-gray-200 rounded shadow-lg mt-14 w-96 max-h-60">
                    {filteredPersons.length === 0 && (
                      <div className="p-3 text-xs text-gray-500">
                        No employees found
                      </div>
                    )}
                    {filteredPersons.map((emp: any, idx: number) => {
                      const alreadyAdded = affectedPersons.some(
                        (e) => e.employeeId === emp.id
                      );
                      return (
                        <HStack
                          key={emp.id}
                          className={`items-center justify-between px-4 py-2 cursor-pointer hover:bg-orange-50 ${
                            idx !== filteredPersons.length - 1
                              ? "border-b border-gray-100"
                              : ""
                          }`}
                        >
                          <div>
                            <div className="font-medium">
                              {emp.employeeName}
                            </div>
                            <div className="text-xs text-gray-400">
                              {emp.email} &middot;{" "}
                              {emp.employeeIdentificationString}
                            </div>
                          </div>
                          <div>
                            {alreadyAdded ? (
                              <Icon
                                icon="mdi:close-circle-outline"
                                className="w-5 h-5 text-red-500"
                                onClick={(e) => {
                                  handleRemovePerson(emp);
                                  e.stopPropagation();
                                  setSearch("");
                                }}
                              />
                            ) : (
                              <Icon
                                icon="mdi:plus-circle-outline"
                                className="w-5 h-5 text-green-500"
                                onClick={(e) => {
                                  handleAddPerson(emp);
                                  e.stopPropagation();
                                  setSearch("");
                                }}
                              />
                            )}
                          </div>
                        </HStack>
                      );
                    })}
                  </Box>
                )}
              </HStack>
            </HStack>
          </HStack>
          <Box className="w-full h-48 mt-2 ag-theme-material">
            <AgGridReact
              rowData={gridData}
              columnDefs={personColDefs}
              onGridReady={onGridReady}
              suppressRowTransform={true}
              suppressClickEdit={true}
              suppressRowClickSelection={true}
              rowClass={
                "border-t border-dashed cursor-pointer hover:bg-slate-50 "
              }
              overlayNoRowsTemplate={
                '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;   margin-top: 50px;">No Rows To Show</span>'
              }
            ></AgGridReact>
          </Box>
        </VStack>
        <HStack className="justify-between pt-6">
          <ButtonPrimary1 onClick={onClose}>Cancel</ButtonPrimary1>
          <ButtonPrimary
            type="submit"
            onClick={() => formik.handleSubmit()}
            loading={isAddLoading || isEditLoading}
            disabled={affectedPersons.length === 0}
          >
            Save
          </ButtonPrimary>
        </HStack>
      </VStack>
    </Box>
  );
}

export default AddOrEditDesignatedPersons;
